<template>
  <div class="px- overflow-hidden">
    <div class="relative py-[28%] disableClippedBg -mb-[15%] overflow-hidden">
      <!-- Error State -->
      <div
        v-if="!loading && (error || !Array.isArray(ads))"
        class="flex items-center justify-center text-red-500 cursor-pointer absolute top-0 left-0 w-full h-full gap-2 pb-[15%]"
        @click="refreshAds"
      >
        <div class="font-bold text-lg">Publicités non chargées</div>
        <div class="w-6 h-6">
          <ColorImg src="/images/icons/load.png" alt="" class="bg-current" />
        </div>
      </div>

      <!-- Loading State (Shimmer) -->
      <div
        v-else-if="loading"
        class="absolute top-0 left-0 w-full overflow-auto"
      >
        <div class="flex w-max gap-2">
          <div class="w-[67vw]">
            <div class="shimmerElement py-[28%]"></div>
          </div>
          <div class="w-[67vw]">
            <div class="shimmerElement py-[28%]"></div>
          </div>
          <div class="w-[67vw]">
            <div class="shimmerElement py-[28%]"></div>
          </div>
        </div>
      </div>

      <!-- SwipperView Carousel -->
      <div v-else class="w-full h-full absolute left-0 top-0">
        <!-- :key="adsKey" -->
        <SwipperView
          :slidesPerView="1.5"
          :nbTabs="displayAds.length"
          :centeredSlides="true"
          :autoPlay="6000"
          :loop="true"
          :defaultTab="1"
          class="h-full"
        >
          <template #default="{ position }">
            <div class="relative py-[28%]">
              <div class="absolute w-full h-full top-0 left-0 px-1">
                <div class="w-full h-full rounded-2xl overflow-hidden">
                  <div
                    class="h-full overflow-hidden pubMainContainer opacity-100 relative"
                  >
                    <div class="w-full h-full pubsContainerCliped">
                      <component
                        :is="currentPub(position).url ? 'a' : 'div'"
                        :href="currentPub(position).url"
                        :target="currentPub(position).url ? '_blank' : ''"
                        @click="handleAdClick(currentPub(position))"
                        class="w-full h-full"
                      >
                        <ImgComponent
                          :src="currentPub(position).picture"
                          alt=""
                          class="w-full h-full object-cover"
                        />
                      </component>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </SwipperView>
      </div>
    </div>
    <div class="py-2"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import { useRouter } from 'vue-router';

const router = useRouter();

const homeStore = getStore('home');

// Computed properties for store state
const ads = computed(() => homeStore.state.ads.items);
const loading = computed(() => homeStore.state.ads.loading);
const error = computed(() => homeStore.state.ads.error);

// Display ads - use shimmers when loading, ads when loaded
const displayAds = computed(() => {
  if (loading.value) {
    return [
      { shimmer: true, picture: '' },
      { shimmer: true, picture: '' },
      { shimmer: true, picture: '' }
    ];
  }
  return Array.isArray(ads.value) && ads.value.length > 0
    ? ads.value
    : [{ picture: '' }, { picture: '' }, { picture: '' }];
});

// Get current pub for display based on position
function currentPub(position) {
  if (displayAds.value.length === 0) return { picture: '', url: '' };
  return displayAds.value[Math.abs(position) % displayAds.value.length];
}

// Key for forcing re-render
const adsKey = ref(0);

// Handle ad clicks
function handleAdClick(pub) {
  console.log('[Publicities] handleAdClick:', pub);

  const actionType = pub.actionType || '';

  // 1. External URL
  if (actionType === 'external' || (!actionType && pub.linkUrl)) {
    if (pub.linkUrl) {
      window.open(pub.linkUrl, '_blank');
    }
    return;
  }

  // 2. Internal Page
  if (actionType === 'internal' || (!actionType && pub.internalPage)) {
    const pageId = pub.internalPage || '';
    if (pageId) {
      router.push(`/${pageId}`);
      // bus.emit(constants.EVENTS.NAVIGATION.BOTTOM_NAV_REQUIRED, {
      //   id: pageId
      // });
    }
    return;
  }

  // 3. Coupon
  if (actionType === 'coupon' || (!actionType && pub.coupon)) {
    const couponCode =
      typeof pub.coupon === 'object' ? pub.coupon?.code : pub.coupon;
    if (couponCode) {
      bus.emit(constants.EVENTS.CART.PROMO_CODE_APPLY, {
        code: couponCode,
        autoApply: true
      });
    }
    return;
  }

  // 4. Dish Detail
  if (actionType === 'dish' || (!actionType && pub.dishId)) {
    if (pub.dishId) {
      bus.emit(constants.EVENTS.DISH.SHOW_DISH_DETAIL, {
        id: pub.dishId
      });
    }
    return;
  }

  // 5. Restaurant Detail
  if (actionType === 'restaurant' || (!actionType && pub.restaurantId)) {
    if (pub.restaurantId) {
      bus.emit(constants.EVENTS.RESTAURANT.SHOW_RESTAURANT_DETAIL, {
        id: pub.restaurantId
      });
    }
    return;
  }

  // 6. Feature
  if (actionType === 'feature' || (!actionType && pub.feature)) {
    if (pub.feature === 'openCart') {
      bus.emit(constants.EVENTS.CART.SHOW);
    }
    if (pub.feature === 'openFavorites') {
      router.push('/favorites');
    }
    if (pub.feature === 'openOrders') {
      router.push('/orders');
    }
    return;
  }
}

// Refresh ads
function refreshAds() {
  bus.emit(constants.EVENTS.HOME.ADS_LOAD);
}

onMounted(() => {
  // Load ads when component mounts
  bus.emit(constants.EVENTS.HOME.ADS_LOAD);
});
</script>

<style scoped>
.pubsContainerCliped {
  clip-path: polygon(0% 0%, 100% 0%, 100% 85%, 0% 100%);
}

.disableClippedBg .pubsContainerCliped {
  clip-path: none;
}

/* Shimmer animation */
@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

.shimmerElement {
  background: linear-gradient(
    to right,
    #f6f7f8 0%,
    #edeef1 20%,
    #f6f7f8 40%,
    #f6f7f8 100%
  );
  background-size: 1000px 100%;
  animation: shimmer 2s infinite linear;
}
</style>
